/**
 * Registration page. Collects email + password + confirmation, validates
 * client-side, then delegates to the auth store. On success the user is signed
 * in and redirected to the dashboard.
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import Input from '@/components/Forms/Input';
import Button from '@/components/Forms/Button';
import Alert from '@/components/Alerts/Alert';
import { useToast } from '@/components/Alerts/toastContext';
import { useAuthStore } from '@/store/authStore';
import { validateEmail, validatePassword } from '@/utils/validators';
import Logo from '@/components/common/Logo';

interface FieldErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
}

/** Email/password sign-up form. */
export default function Register(): ReactElement {
  const navigate = useNavigate();
  const toast = useToast();

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const register = useAuthStore((s) => s.register);
  const loading = useAuthStore((s) => s.loading);
  const error = useAuthStore((s) => s.error);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const validate = (): boolean => {
    const errors: FieldErrors = {};
    if (!validateEmail(email)) errors.email = 'Email không hợp lệ';
    const pwdError = validatePassword(password);
    if (pwdError) errors.password = pwdError;
    if (confirmPassword !== password) errors.confirmPassword = 'Mật khẩu xác nhận không khớp';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await register(email.trim(), password);
      toast.success('Tạo tài khoản thành công');
      navigate('/', { replace: true });
    } catch {
      toast.error('Đăng ký thất bại');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Logo className="mx-auto mb-3 h-14 w-14" />
          <h1 className="text-2xl font-bold text-gray-900">Ads360</h1>
          <p className="mt-1 text-sm text-gray-500">Tạo tài khoản để bắt đầu</p>
        </div>

        <div className="card">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Đăng ký</h2>

          {error && (
            <Alert type="error" className="mb-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="ban@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={fieldErrors.email}
              required
            />
            <Input
              label="Mật khẩu"
              type="password"
              autoComplete="new-password"
              placeholder="Ít nhất 8 ký tự"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={fieldErrors.password}
              required
            />
            <Input
              label="Xác nhận mật khẩu"
              type="password"
              autoComplete="new-password"
              placeholder="Nhập lại mật khẩu"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={fieldErrors.confirmPassword}
              required
            />

            <Button type="submit" loading={loading} className="w-full">
              Đăng ký
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Đã có tài khoản?{' '}
            <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
              Đăng nhập
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
